// Claim guidance utilities: rating-criteria summaries, claim-risk detection,
// pasteable 526EZ language. Used by the Claim Tracker view and the master PDF.

import { conditionLibrary } from "./claimData";
import { evidenceElements, getConditionResources } from "./claimData";
import { computePillarStatus } from "./claimUtils";

// ---- Rating-criteria summary ----
// Given an issue (claimed condition) + the user's est. rating, return a short
// human-readable description of what rating tier they're aiming for plus the
// available rating levels for that DC.
export function getRatingTierSummary(issue) {
  if (!issue) return null;
  const cond = conditionLibrary.find((c) => c.code === issue.code);
  const levels = (cond?.r?.length ? cond.r : [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]);
  const target = Number(issue.estimatedRating || 0);
  const res = getConditionResources(issue.code, issue.system);
  return {
    target,
    levels,
    cfrRef: res.cfrRef || "38 CFR Part 4",
    dbqName: res.dbqName || "VA DBQ",
    higherLevels: levels.filter((r) => r > target),
    isMaxed: target > 0 && target === Math.max(...levels),
  };
}

// ---- Evidence-presence summary per pillar (for one condition) ----
// Returns { have: N, total: 4, perPillar: [{id,label,have,count}] }
export function getEvidencePresenceSummary(issue, evidence = [], ctx = {}) {
  const perPillar = evidenceElements.map((el) => {
    const count = evidence.filter((ev) => ev.issueIds?.includes(issue.id) && ev.element === el.id).length;
    const status = computePillarStatus(issue, el.id, evidence, ctx);
    return { id: el.id, label: el.label, have: status === "have", count };
  });
  return {
    have: perPillar.filter((p) => p.have).length,
    total: perPillar.length,
    perPillar,
  };
}

// ---- Claim risk detection ----
// Returns an array of risk objects: { id, severity: "high"|"med"|"low", title, detail, action }
// Detected risks:
//   - Pyramiding (claiming overlapping DCs for the same body region)
//   - Stale evidence > 12 months for required pillars (likely denial)
//   - Theory mismatch (Secondary theory but no primary condition exists)
//   - Vague functional impact (< 60 chars) — examiner downgrade risk
//   - Missing required pillar with no in-progress symptom log
//   - Tinnitus alone capped at 10% (not denial, just clarity)

const TWELVE_MONTHS_DAYS = 365;

// Pyramiding rule pairs: claiming both = examiner will pick one.
// Each entry: [codeA, codeB, reason]
const PYRAMIDING_PAIRS = [
  ["5237", "5242", "Both lumbar strain (5237) and degenerative arthritis of the spine (5242) cover the same back. VA will rate the higher one only."],
  ["5237", "5243", "Lumbar strain (5237) and intervertebral disc syndrome (5243) overlap. Pick the one that best fits — IVDS pays separately for incapacitating episodes."],
  ["5242", "5243", "Spinal arthritis (5242) and IVDS (5243) cover the same area. Examiner will choose one."],
  ["9411", "9434", "PTSD (9411) and major depressive disorder (9434) are both rated under the General Rating Formula. VA will combine into a single mental health rating, not stack them."],
  ["9411", "9400", "PTSD (9411) and generalized anxiety (9400) are both under § 4.130 — single combined mental health rating."],
  ["6260", "6204", "Tinnitus (6260) and peripheral vestibular disorders (6204) often overlap. Confirm distinct medical findings before claiming both."],
];

export function detectClaimRisks({ issue, allIssues = [], evidence = [], symptoms = [] }) {
  const risks = [];
  if (!issue) return risks;

  // 1. Pyramiding
  PYRAMIDING_PAIRS.forEach(([a, b, reason]) => {
    if (issue.code !== a && issue.code !== b) return;
    const other = issue.code === a ? b : a;
    const partner = allIssues.find((i) => i.id !== issue.id && i.code === other);
    if (partner) {
      risks.push({
        id: `pyr-${issue.id}-${other}`,
        severity: "high",
        kind: "pyramiding",
        title: `Pyramiding risk with DC ${other}`,
        detail: reason,
        action: `Drop one or merge into a single combined claim. Keep the DC that better matches your symptoms and rating tier.`,
      });
    }
  });

  // 2. Theory mismatch — Secondary claim with no plausible primary
  const theory = (issue.theory || "").toLowerCase();
  if (theory.includes("secondary")) {
    const otherIssues = allIssues.filter((i) => i.id !== issue.id);
    if (otherIssues.length === 0) {
      risks.push({
        id: `sec-${issue.id}`,
        severity: "high",
        kind: "theory",
        title: "Secondary theory has no primary condition",
        detail: "You marked this as secondary to another service-connected condition, but no primary condition is in this workspace. VA will deny secondary claims without an established primary.",
        action: "Add the primary service-connected condition first, then re-file this as secondary to it.",
      });
    }
  }

  // 3. Vague functional impact
  const impact = (issue.functionalImpact || "").trim();
  if (impact.length > 0 && impact.length < 60) {
    risks.push({
      id: `vague-${issue.id}`,
      severity: "med",
      kind: "vague",
      title: "Functional impact is too short",
      detail: "C&P examiners and rating specialists rely on specific functional descriptions (frequency, duration, severity, what it stops you from doing). One short line usually leads to a low-tier rating.",
      action: "Expand to at least 60 characters. Cover: how often, how long, what daily tasks are affected, what makes it worse, what coping methods you use.",
    });
  } else if (!impact) {
    risks.push({
      id: `noimpact-${issue.id}`,
      severity: "med",
      kind: "vague",
      title: "No functional impact recorded",
      detail: "Without a functional-impact statement, VA defaults to the lowest tier supported by raw exam findings.",
      action: "Add a paragraph describing how this condition affects work, sleep, mobility, and daily tasks.",
    });
  }

  // 4. Stale-evidence-only on required pillars (current diagnosis or severity)
  const issueEv = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
  const today = new Date();
  ["current", "severity"].forEach((pillarId) => {
    const pillarEv = issueEv.filter((ev) => ev.element === pillarId);
    if (!pillarEv.length) return;
    const newestDays = Math.min(
      ...pillarEv.map((ev) => {
        const d = ev.date ? new Date(ev.date) : null;
        if (!d || Number.isNaN(d.getTime())) return Infinity;
        return Math.round((today - d) / (1000 * 60 * 60 * 24));
      }),
    );
    if (newestDays > TWELVE_MONTHS_DAYS && newestDays !== Infinity) {
      const pillar = evidenceElements.find((e) => e.id === pillarId)?.label || pillarId;
      risks.push({
        id: `stale-${issue.id}-${pillarId}`,
        severity: "high",
        kind: "stale",
        title: `${pillar} evidence is over 12 months old`,
        detail: `Your newest "${pillar}" evidence is ${newestDays} days old. VA frequently wants current evidence (within the past year) for severity and current-diagnosis pillars.`,
        action: "Schedule an updated VA or private appointment. Ask for a current findings letter and add it as new evidence.",
      });
    }
  });

  // 5. Missing required pillars — surfaced as "Service connection risk" by the riskEngine.
  // Intentionally NOT emitting a "Likely denial" panel here anymore — the new dual
  // Service-Connection / Rating risk panel handles this with precise language.

  // 6. Tinnitus capped clarity
  if (issue.code === "6260" && Number(issue.estimatedRating || 0) > 10) {
    risks.push({
      id: `tinn-${issue.id}`,
      severity: "low",
      kind: "rating",
      title: "Tinnitus is capped at 10%",
      detail: "Under 38 CFR 4.87 DC 6260, tinnitus has a flat 10% rating whether unilateral or bilateral. Estimating higher will mislead your math.",
      action: "Set estimated rating to 10%.",
    });
  }

  // 7. Aggregate vs symptom log — celebrate if symptom log is helping
  if (symptoms?.length) {
    const linkedSymptoms = symptoms.filter((s) => s.linkedIssueId === issue.id);
    if (linkedSymptoms.length >= 5) {
      risks.push({
        id: `support-${issue.id}`,
        severity: "info",
        kind: "support",
        title: `${linkedSymptoms.length} symptom log entries support this claim`,
        detail: "Your symptom journal builds a credible severity record. Bring the printable log to your C&P exam.",
        action: "",
      });
    }
  }

  return risks;
}

// ---- Combined-rating preview ----
// Given existing combined rating + a new rating to add, return what the new
// VA-combined number will be after rounding. Used in "what gets combined" UX.
export function previewCombined(existingCombinedRaw, newRating) {
  const r = Math.max(0, Math.min(100, Number(newRating || 0)));
  if (!r) return { raw: existingCombinedRaw, rounded: Math.min(100, Math.round(existingCombinedRaw / 10) * 10) };
  const combined = existingCombinedRaw + (100 - existingCombinedRaw) * (r / 100);
  return { raw: combined, rounded: Math.min(100, Math.round(combined / 10) * 10) };
}

// ---- Pasteable 526EZ Section IV language ----
// Builds a 2-3 paragraph paste-block ready for the official 21-526EZ Section IV
// (claimed conditions / remarks) field.
export function buildPasteable526EZLanguage(issue, { evidence = [], statements = [] } = {}) {
  const res = getConditionResources(issue.code, issue.system);
  const issueEv = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
  const lines = [];

  // Header
  const claimedAs = (issue.claimedAs || "(condition not specified)").trim();
  lines.push(`Claimed condition: ${claimedAs}`);
  lines.push(`Diagnostic code: ${issue.code || "(none)"}  |  Rating reference: ${res.cfrRef || "38 CFR Part 4"}`);
  if (issue.theory) lines.push(`Theory of service connection: ${issue.theory}`);
  if (issue.onset) lines.push(`Date / event of onset: ${issue.onset}`);
  lines.push("");

  // Functional impact
  const impact = (issue.functionalImpact || "").trim();
  if (impact) {
    lines.push("Current symptoms and functional impact:");
    lines.push(impact);
  } else {
    lines.push("Current symptoms and functional impact: (add a paragraph describing how this condition affects work, sleep, mobility, and daily activities)");
  }
  lines.push("");

  // Evidence summary
  if (issueEv.length) {
    lines.push("Supporting evidence attached / referenced:");
    issueEv.slice(0, 8).forEach((ev) => {
      const dateStr = ev.date ? ` (${ev.date})` : "";
      const src = ev.source ? ` — ${ev.source}` : "";
      lines.push(`  - ${ev.title || "Evidence item"}${dateStr}${src}`);
    });
    if (issueEv.length > 8) lines.push(`  - …and ${issueEv.length - 8} additional item(s)`);
  } else {
    lines.push("Supporting evidence: (none attached yet — VA will request records before deciding)");
  }
  lines.push("");

  // Statement reference
  const myStatements = statements.filter((s) => {
    const t = (s.title || "").toLowerCase();
    return t.includes(claimedAs.toLowerCase().split(" ")[0]) || (s.linkedIssueId === issue.id);
  });
  if (myStatements.length) {
    lines.push(`Personal statement(s) attached: ${myStatements.map((s) => s.title || s.type).join("; ")}`);
  }

  // Estimated rating
  if (issue.estimatedRating) {
    lines.push(`Veteran-estimated rating tier: ${issue.estimatedRating}%`);
  }

  lines.push("");
  lines.push("(Generated by ClaimOps VA - planning tool. Paste into the appropriate section of VA Form 21-526EZ. Verify all DC, CFR, and DBQ references at VA.gov / eCFR.gov before submitting.)");

  return lines.join("\n");
}
